import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import Banner from "@/components/Banner";
import { Hash, RefreshCw, Save } from "lucide-react";

const TYPES = ["Full-time", "Intern", "Contract", "Part-time", "Candidate"];

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

export default function EmployeeIdPrefixes() {
  const [companyId, setCompanyId] = useState("company");
  const [companies, setCompanies] = useState([]);
  const [config, setConfig] = useState({});
  const [nextCodes, setNextCodes] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async (cid = companyId) => {
    setBusy(true);
    setErr("");
    try {
      const [cRes, cfgRes] = await Promise.all([
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/organization/employee-id-config", { params: { company_id: cid } }),
      ]);
      setCompanies(cRes.data || []);
      setCompanyId(cfgRes.data.company_id || cid);
      setConfig(cfgRes.data.config || {});
      setNextCodes(cfgRes.data.next_codes || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load employee ID settings");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setRow = (type, patch) => {
    setConfig((prev) => ({
      ...prev,
      [type]: { ...(prev[type] || {}), ...patch },
    }));
  };

  const save = async () => {
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      const r = await api.put("/organization/employee-id-config", {
        company_id: companyId,
        config,
      });
      setConfig(r.data.config || {});
      setNextCodes(r.data.next_codes || {});
      setMsg("Employee ID prefixes saved. New hires and intern conversions will use these sequences.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title="Employee ID Prefixes" breadcrumbs={["Home", "Organization", "Employee ID Prefixes"]}>
      <div className="space-y-4" data-testid="employee-id-prefixes-page">
        <Banner variant="error" onClose={() => setErr("")}>{err}</Banner>
        <Banner variant="success" onClose={() => setMsg("")}>{msg}</Banner>

        <div className="bg-white border border-slate-200 rounded p-4 space-y-3">
          <p className="text-sm text-slate-600">
            Set a prefix and number width for each employment type, plus Candidate (pre-hire).
            Employee codes (EMP/INT/…) are assigned when creating an employee or hiring a candidate.
            Candidate codes (CND) are separate and do not consume employee sequences.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Company</label>
              <select
                className={inputCls}
                value={companyId}
                onChange={(e) => { setCompanyId(e.target.value); load(e.target.value); }}
                data-testid="eid-company"
              >
                {(companies.length ? companies : [{ id: companyId, name: companyId }]).map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.id}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => load(companyId)}
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-50"
              data-testid="eid-save"
            >
              <Save className="h-4 w-4" /> Save prefixes
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Employment type</th>
                <th className="text-left px-4 py-3">Prefix</th>
                <th className="text-left px-4 py-3">Separator</th>
                <th className="text-left px-4 py-3">Digits</th>
                <th className="text-left px-4 py-3">Next code (preview)</th>
              </tr>
            </thead>
            <tbody>
              {TYPES.map((type) => {
                const row = config[type] || { prefix: "", pad: 3, separator: "" };
                return (
                  <tr key={type} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                      <Hash className="h-4 w-4 text-[#226DB4]" /> {type}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        className={inputCls}
                        value={row.prefix || ""}
                        onChange={(e) => setRow(type, { prefix: e.target.value.toUpperCase() })}
                        data-testid={`eid-prefix-${type}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        className={inputCls}
                        value={row.separator ?? ""}
                        onChange={(e) => setRow(type, { separator: e.target.value })}
                        placeholder="(none)"
                        data-testid={`eid-sep-${type}`}
                      />
                    </td>
                    <td className="px-4 py-3 w-28">
                      <input
                        type="number"
                        min={1}
                        max={8}
                        className={inputCls}
                        value={row.pad ?? 3}
                        onChange={(e) => setRow(type, { pad: Number(e.target.value) || 3 })}
                        data-testid={`eid-pad-${type}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-[#226DB4] font-semibold">
                      {nextCodes[type] || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
